import { Browser, DeviceInfo } from "Device/Info/DeviceInfo";
import { EOWLocalizableException } from "../../../OW.Remoting/Basic/OWExceptions";
import { MemoryUtils } from "../Utils/MemoryUtils";
import { IStreamDecryptor } from "./IStreamDecryptor";
import { IStreamEncryptor } from "./IStreamEncryptor";
import { SecurityUtils } from "./SecurityUtils";

class AESCBCStreamDecryptor implements IStreamDecryptor {
    private _iv: ArrayBuffer;
    private _prevDataOverflow: Uint8Array;

    constructor(private _key: CryptoKey, iv?: BufferSource) {
        if (this._key.algorithm.name !== "AES-CBC")
            throw new Error(`Unsupported algorithm: ${this._key.algorithm}!`); //`

        this._iv = iv ? MemoryUtils.bufferSourceToArrayBuffer(iv) : new ArrayBuffer(0);
        this._prevDataOverflow = new Uint8Array;
    }

    private async _decrypt(decryptData: { name: string; iv: ArrayBuffer }, key: CryptoKey, data: ArrayBuffer | Uint8Array): Promise<ArrayBuffer> {
        if (DeviceInfo.deviceProperties.browser === Browser.safari)
            return this._decryptPolyfill(key, new Uint8Array(decryptData.iv), new Uint8Array(data));
        else
            return self.crypto.subtle.decrypt(decryptData, key, data);
    }

    /**
     * Polyfill pro dešifrování AES-CBC v Safari, které přestalo vyhazovat výjimku při dešifrování špatným klíčem.
     * Místo toho vrací náhodná data. Nejsme tak schopni detekovat neúspěšné dešifrování.
     * @see https://bugs.webkit.org/show_bug.cgi?id=211777
     */
    private async _decryptPolyfill(key: CryptoKey, iv: Uint8Array, data: Uint8Array): Promise<ArrayBuffer> {
        await SecurityUtils.loadAsmCryptoPolyfill();
        const keyData = await self.crypto.subtle.exportKey("raw", key);
        //@ts-ignore asmcrypto není modul, načte se do globálních proměnných
        const cbc = new AES_CBC(new Uint8Array(keyData), iv);
        return cbc.decrypt(data);
    }

    //TODO: optimalizovat s TypedArray.subarray
    public async decryptBlock(data: BufferSource, lastBlock?: boolean): Promise<ArrayBuffer> {
        try {
            if (!data || data.byteLength === 0)
                throw new Error("Can not use empty data block!");

            const prevDataOverflowLength = this._prevDataOverflow ? this._prevDataOverflow.length : 0;
            const buffer = MemoryUtils.bufferSourceToArrayBuffer(data);

            const allDataLength = buffer.byteLength + prevDataOverflowLength;
            let lengthToDecrypt = lastBlock ? allDataLength : ((allDataLength / 16) | 0) * 16;
            if (!this._iv && lengthToDecrypt < 32) // pokud data nejsou dost velká na IV + první blok k dešifrování, tak se zpracování nechá na další volání funkce decryptBlock
                lengthToDecrypt = 0;

            let result: ArrayBuffer;

            if (lengthToDecrypt !== 0) {
                // složení dat z přebytku minulého šifrování a aktuálních dat
                let temp = this._prevDataOverflow ? MemoryUtils.join(this._prevDataOverflow, new Uint8Array(buffer, 0, lengthToDecrypt - prevDataOverflowLength)) : new Uint8Array(buffer, 0, lengthToDecrypt);

                if (!this._iv) {
                    this._iv = (temp as any).slice(0, 16);
                    temp = (temp as any).slice(16);
                }

                let lastBlockData: ArrayBuffer | undefined;

                if (!lastBlock) {
                    lastBlockData = (temp as any).slice(temp.byteLength - 16);
                    const encryptData = { name: this._key.algorithm.name!, iv: lastBlockData };
                    const encryptedPaddingBlock = (await self.crypto.subtle.encrypt(encryptData, this._key, emptyPaddingBlock)).slice(0, 16); // konstrukce zarovnávacího bloku PKCS#7 pro dešifrování aktuálního bloku dat
                    temp = MemoryUtils.join(temp, encryptedPaddingBlock);
                }

                const decryptData = { name: this._key.algorithm.name!, iv: this._iv };
                result = await this._decrypt(decryptData, this._key, temp);

                if (result.byteLength > 0 && lastBlockData)
                    this._iv = lastBlockData;
            } else
                result = new ArrayBuffer(0); //pokud první blok je, tak malý, že nejde zarovnat na dešifrovací blok

            const bytesCountToPrev = allDataLength - lengthToDecrypt;
            if (buffer.byteLength >= bytesCountToPrev)
                this._prevDataOverflow = new Uint8Array(buffer, buffer.byteLength - bytesCountToPrev);
            else
                this._prevDataOverflow = new Uint8Array(MemoryUtils.join(this._prevDataOverflow, buffer)); // pokud tento blok i ten předešlý byly dohromady menší než 128bit

            return result;
        } catch {
            throw new EOWLocalizableException("GUI-670193", "GUI-670192"); //Chyba dešifrování, //Data se nepodařilo dešifrovat
        }
    }
}

class AESCBCStreamEncryptor implements IStreamEncryptor {
    public readonly blockSize: number = 16;

    private _iv: ArrayBuffer;
    private _firstBlock: boolean = true;
    private _prevDataOverflow: Uint8Array;

    constructor(private _key: CryptoKey, iv?: BufferSource, private _addIVtoFirstBlock: boolean = true) {
        if (this._key.algorithm.name !== "AES-CBC")
            throw new Error(`Unsupported algorithm: ${this._key.algorithm}!`); //`

        this._prevDataOverflow = new Uint8Array;
        this._iv = (iv ? MemoryUtils.bufferSourceToArrayBuffer(iv) : self.crypto.getRandomValues(new Uint8Array(this.blockSize)).buffer) as ArrayBuffer;
    }

    /**
     * Vypočítá, jak budou velká data po jejich zašifrování
     * @param originalSize Velikost původních dat
     */
    public getEncryptedSize(originalSize: number): number {
        // velikost se zvětší o jeden blok na začátku (IV) a poslední kus dat se zarovná na velikost bloku
        return originalSize - (originalSize % this.blockSize) + 2 * this.blockSize;
        //return (originalSize / 16 | 0) * 16 + 32; // alternativní zápis
    }

    public async encryptBlock(data: BufferSource | undefined | null, lastBlock?: boolean): Promise<ArrayBuffer> {
        const prevDataOverflowLength = this._prevDataOverflow ? this._prevDataOverflow.length : 0;
        const buffer = MemoryUtils.bufferSourceToArrayBuffer(data || new ArrayBuffer(0));
        const allDataLength = buffer.byteLength + prevDataOverflowLength;
        const lengthToEncrypt = lastBlock ? allDataLength : ((allDataLength / this.blockSize) | 0) * this.blockSize;

        let result: ArrayBuffer;

        if (lengthToEncrypt !== 0 || lastBlock) {
            // složení dat z přebytku minulého šifrování a aktuálních dat
            const temp = this._prevDataOverflow ? MemoryUtils.join(this._prevDataOverflow, new Uint8Array(buffer, 0, lengthToEncrypt - prevDataOverflowLength)) : new Uint8Array(buffer, 0, lengthToEncrypt);

            result = await self.crypto.subtle.encrypt({ name: this._key.algorithm.name!, iv: this._iv }, this._key, temp);
            if (!lastBlock)
                result = result.slice(0, result.byteLength - this.blockSize); // odstranění zarovnávacího bloku PKCS#7
        } else
            result = new ArrayBuffer(0); //pokud první blok je, tak malý, že nejde zarovnat na šifrovací blok

        const bytesCountToPrev = allDataLength - lengthToEncrypt;
        if (buffer.byteLength >= bytesCountToPrev)
            this._prevDataOverflow = new Uint8Array(buffer, buffer.byteLength - bytesCountToPrev);
        else
            this._prevDataOverflow = new Uint8Array(MemoryUtils.join(this._prevDataOverflow, buffer)); // pokud tento blok i ten předešlý byly dohromady menší než 128bit

        if (this._firstBlock && this._addIVtoFirstBlock) {
            result = MemoryUtils.join(this._iv, result);
        }

        if (result.byteLength > 0)
            this._iv = result.slice(result.byteLength - this.blockSize);
        this._firstBlock = false;

        return result;
    }
}

const emptyPaddingBlock = new Uint8Array(16).fill(16).buffer as ArrayBuffer;

SecurityUtils.registerStreamCryptor(AESCBCStreamDecryptor, "AES-CBC");
SecurityUtils.registerStreamCryptor(AESCBCStreamEncryptor, "AES-CBC");
